import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import BookPage from "@/pages/BookPage";
import ContactPage from "@/pages/ContactPage";
import GalleryPage from "@/pages/GalleryPage";
import BlogPage from "@/pages/BlogPage";
import SpeakingPage from "@/pages/SpeakingPage";
import BookingPage from "@/pages/BookingPage";
import ConsultationPage from "@/pages/ConsultationPage";
import AdminPage from "@/pages/AdminPage";

/* ─── SEO Meta Tags Component ──────────────────────────────── */
const SEO = () => {
  const location = useLocation();

  useEffect(() => {
    const meta = {
      "/": {
        title: "Paradigm Global Business Solutions | Your All-In-One Business Resource Center",
        description: "Being an Entrepreneur Shouldn't Mean BEING Alone. We provide business strategy, insurance, funding, analytics, and coaching — nationwide and internationally.",
      },
      "/about": {
        title: "About Us | Paradigm Global Business Solutions",
        description: "Founded by Ekeyah Wallace, Paradigm combines business strategy, risk management, and financial literacy to help entrepreneurs at every stage.",
      },
      "/services": {
        title: "Our Services — 9 Success Avenues | Paradigm Global Business Solutions",
        description: "9 service areas across 3 programs: Business Logic, Insurance + Assurance, and Paradigm Shift Architects. Comprehensive support from startup to succession.",
      },
      "/book": {
        title: "Packages & Book a Call | Paradigm Global Business Solutions",
        description: "Choose from Small Business or Enterprise packages. Book a free strategy session with Paradigm — no obligation, just genuine support.",
      },
      "/contact": {
        title: "Contact Us | Paradigm Global Business Solutions",
        description: "Reach us at (863) 585-1412 or ekeyah@paradigmcfsolutions.com. Lakeland, FL — serving nationwide and internationally.",
      },
      "/gallery": {
        title: "Social Gallery | Paradigm Global Business Solutions",
        description: "Stay connected with Paradigm across YouTube, Instagram, TikTok, and more.",
      },
      "/speaking": {
        title: "Paradigm Shift Architects — Speaking Division | Paradigm Global Business Solutions",
        description: "Boss Talk, FINA-Speak, Wealth Building Champions, and Wellness Warriors. Transformative speaking engagements for corporations and entrepreneurs.",
      },
      "/blog": {
        title: "Blog — Business Insights & Articles | Paradigm Global Business Solutions",
        description: "Business tips, success stories, and entrepreneurial insights from the Paradigm team.",
      },
      "/consultation": {
        title: "Book a Consultation | Paradigm Global Business Solutions",
        description: "Schedule your free Paradigm Global Business Strategy Session. Complete our brief intake form and book a time with our advisory team.",
      },
    };

    const page = meta[location.pathname] || meta["/"];
    document.title = page.title;

    let descTag = document.querySelector('meta[name="description"]');
    if (!descTag) {
      descTag = document.createElement("meta");
      descTag.name = "description";
      document.head.appendChild(descTag);
    }
    descTag.content = page.description;

    // Open Graph
    const ogTags = { "og:title": page.title, "og:description": page.description, "og:type": "website", "og:url": window.location.href };
    Object.entries(ogTags).forEach(([prop, content]) => {
      let tag = document.querySelector(`meta[property="${prop}"]`);
      if (!tag) { tag = document.createElement("meta"); tag.setAttribute("property", prop); document.head.appendChild(tag); }
      tag.content = content;
    });
  }, [location]);

  return null;
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash.replace("#", ""));
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
};

const Layout = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  return (
    <>
      <ScrollToTop />
      <SEO />
      <Navbar />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/services/:serviceId" element={<ServicesPage />} />
        <Route path="/book" element={<BookPage />} />
        <Route path="/booking" element={<BookingPage />} />
        <Route path="/consultation" element={<ConsultationPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/speaking" element={<SpeakingPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
      {!isAdmin && <Footer />}
      <Toaster position="top-right" richColors />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Layout />
      </BrowserRouter>
    </div>
  );
}

export default App;
